import { geometry } from 'app/contracts/Geometry';
import { particles } from 'app/contracts/Particles';
import { Inertia } from 'utils/motion/Inertia';
import { ParticleRepell } from 'particles/behaviour/ParticleRepell';
import { ParticleBehaviour } from '../ParticleBehaviour';
import { ParticleMouseParallax } from './ParticleMouseParallax';

export class ParticleDepth extends ParticleBehaviour {
	private _depthConfig: any = { min: 1, max: 10, value: 1, ratio: 1, ratioX: 1, xfactor: 1, yfactor: 0.5 };
	private _depthValue: Inertia;
	private _scale: any = { min: 0.1, max: 0.43 };
	private _alpha: any = { min: 0.1, max: 0.7 };
	private _shift: any = { x: 0, y: 0 };
	private _perpectiveArea: any;
	private _orginalCords: geometry.Vector2;
	private _bounce: boolean = true;
	private _bounceDirection: number = 1;
	private _bounceSpeed: number = 0.01;
	private _mouseParallax: ParticleMouseParallax;
	private _repell: ParticleRepell;

	constructor(
		sceneProps: particles.ParticlesSceneProps,
		particleProps: particles.ParticleProps,
		animatedProps: particles.ParticleProperties
	) {
		super(sceneProps, particleProps, animatedProps);
		this._depthValue = new Inertia(5, 1);

		this._bounceDirection = Math.random() < 0.5 ? 1 : -1;


		this._bounceSpeed = 0.01 + Math.random() * 0.09;
	}

	//GETTER / SETTER
	//-------------------------------------------------------------------------------------

	public set mouseParallax(mouseParallax: ParticleMouseParallax) {
		this._mouseParallax = mouseParallax;
	}

	public set repell(repell: ParticleRepell) {
		this._repell = repell;
	}

	public set scaleConfig(config: any) {
		this._scale = config;
	}

	public set alphaConfig(config: any) {
		this._alpha = config;
	}

	public set bounce(value: boolean) {
		this._bounce = value;
	}

	public set trackMouse(value: boolean) {
	}

	public set depth(v: number) {
		this._depthValue.target = this._depthValue.value = v;
		this.tick();
	}

	public set perpectiveArea(v: any) {
		this._perpectiveArea = v;
	}

	public set orginalCords(v: geometry.Vector2) {
		this._orginalCords = v;
	}

	public get orginalCords(): geometry.Vector2 {
		return this._orginalCords;
	}

	public getRatio(): number {
		return this._depthConfig.ratio;
	}

	//PUBLIC
	//-------------------------------------------------------------------------------------
	public setDepth(depth: number, ease: boolean = false): void {
		if (!ease) {
			this.depth = depth;
			return;
		}

		this._depthValue.target = depth;

		this.tick();
	}

	/* Translate x/y cords to their depth value versions
	 * This will essentially shift down a standard X/Y
	 */
	public translateCords(cords: geometry.Vector2, perpectiveArea: any, trace: boolean): geometry.Vector2 {
		//X shift will go ---> when cords.x under perpectiveArea.center.x
		//X shift will go <--- when cord.x over perpectiveArea.center.x
		//X shift will be zero when cord.x === perpectiveArea.center.x;
		//At startX shift left will be at +100%
		//At endX shift right will be -100%
		//so need to get ratio in range of 1 to -1
		let startX = perpectiveArea.center.x - perpectiveArea.w / 2;
		let endX = startX + perpectiveArea.w;
		let effectiveWidth = (perpectiveArea.w / 2) * ((1 - this._depthConfig.ratio) * perpectiveArea.shift);
		let ratioXRaw = (cords.x - startX) / (endX - startX);
		let ratioX = (ratioXRaw - 0.5) * 2 * -1;

		this._depthConfig.ratioX = ratioX;
		this._shift.x = ratioX * effectiveWidth * this._depthConfig.xfactor;

		if (trace) {
		}

		let zWidth = 0.04;
		let startY = perpectiveArea.center.y - perpectiveArea.h / 2;
		let endY = startY + perpectiveArea.h;
		let effectiveHeight = (perpectiveArea.h / 2) * ((1 - this._depthConfig.ratio) * perpectiveArea.shift);
		let ratioYRaw = (cords.y - startY) / (endY - startY);
		let ratioY = (ratioYRaw - 0.5) * 2 * -1;
		this._shift.y =
			zWidth * ratioY * (1 - this._depthConfig.ratio) + ratioY * effectiveHeight * this._depthConfig.yfactor;

		if (trace) {
		}

		return {
			x: cords.x + this._shift.x,
			y: cords.y + this._shift.y
		};
	}

	public getAlpha() {
		return this._alpha.min + (this._alpha.max - this._alpha.min) * this._depthConfig.ratio;
	}

	public getScale() {
		return this._scale.min + (this._scale.max - this._scale.min) * this._depthConfig.ratio;
	}

	public tick(): void {
		if (this._bounce) {
			this._processBounce();
		}

		this._depthValue.tick();

		this._depthConfig.ratio =
			1 - (this._depthValue.value - this._depthConfig.min) / (this._depthConfig.max - this._depthConfig.min);

		//Position:

		let cords = this._sceneProps.flags.boxHoverx
			? {
					x: this._sceneProps.targets.mouse.relativePosition.x + 0.6,
					y: this._sceneProps.targets.mouse.relativePosition.y
				}
			: this._orginalCords;

		if (this._sceneProps.flags.boxHoverx) {
			if (cords.x > 0.808) cords.x = 0.808;
			else if (cords.x < 0.792) cords.x = 0.792;

			if (cords.y < 0.32) cords.y = 0.32;
			else if (cords.y > 0.43) cords.y = 0.43;
		}

		let translatedCords = this.translateCords(cords, this._perpectiveArea, false);
		this._particleProps.ref.position.set(translatedCords);

		//Alpha / scale:
		this._animatedProps.targetAlpha = this.getAlpha();
		this._animatedProps.targetScale = this.getScale();

		//Mouse Parallax and Repell
		this._setParallax();
		this._setRepell();
	}

	private _processBounce() {
		this._depthValue.target += this._bounceSpeed * this._bounceDirection;

		//Reverse if at bounds:
		if (
			(this._bounceDirection == -1 && this._depthValue.target < this._depthConfig.min) ||
			(this._bounceDirection == 1 && this._depthValue.target > this._depthConfig.max)
		) {
			this._bounceDirection *= -1;
		}
	}

	/** Parallax needs to damped as it gets to center and further back it is */
	private _setParallax() {
		if (this._mouseParallax.active) {
			this._mouseParallax.setup({
				ease: { x: 20, y: 20 },
				power: { x: this._depthConfig.ratio * 0.08, y: this._depthConfig.ratio * 0.2 }
			});
		}
	}

	/** Repell needs to be damped by depth (otherwise it looks odd) */
	private _setRepell() {
		if (this._repell.active) {
			this._repell.radius = this._depthConfig.ratio * 69;
			this._repell.randomisePower(this._depthConfig.ratio * 60, this._depthConfig.ratio * 20);
		}
	}
}
